import PropTypes from 'prop-types';
import './ReplyBlot';
import './reply-blot-styles.css';
import 'quill-mention';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Switch, Tooltip, FormControlLabel, FormGroup, Grid, IconButton, Box, CircularProgress } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import ReactQuill from 'react-quill';
import { useTabs } from '../../context/TabContext';
import '../../utils/highlight';
import EditorToolbar, { formats } from './EditorToolbar';
import { StyledEditor } from './styles';
import { AwaitButton } from '../await-button';
import Iconify from '../iconify';
import FileAttachment from '../file-attachment/FileAttachment';
import PopoutLinkSet from './PopoutLinkSet';
import './CustomQuillBlots/EmojiBlot';
import AIAssistButton from './AIAssistButton';
import { useSettingsCheck } from '../settings';

// ----------------------------------------------------------------------

Editor.propTypes = {
  id: PropTypes.string,
  sx: PropTypes.object,
  error: PropTypes.bool,
  simple: PropTypes.bool,
  removeHyperLink: PropTypes.bool,
  noFormat: PropTypes.bool,
  verySimple: PropTypes.bool,
  none: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  helperText: PropTypes.object,
  users: PropTypes.array,
  handleFileInputChange: PropTypes.func,
  handleSend: PropTypes.func,
  disabled: PropTypes.bool,
  attachFile: PropTypes.bool,
  emailSwitch: PropTypes.bool,
  sendButton: PropTypes.bool,
  sendIcon: PropTypes.bool,
  ticketEmailsOn: PropTypes.bool,
  setTicketEmailsOn: PropTypes.func,
  disableSend: PropTypes.bool,
  disableSendTooltip: PropTypes.string,
  addItemsToTicket: PropTypes.bool,
  setAddItemsToTicket: PropTypes.func,
  addItemsLabel: PropTypes.string,
  displayedTicket: PropTypes.number,
  currentTicket: PropTypes.number,
  attachments: PropTypes.array,
  removeFile: PropTypes.func,
  shouldFocus: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  chatBoxEditor: PropTypes.bool,
  replyMessage: PropTypes.object,
  onReplyRemove: PropTypes.func,
  printChat: PropTypes.bool,
  aiHelp: PropTypes.bool,
  prependText: PropTypes.object,
  editorRef: PropTypes.object,
};

export default function Editor({
  id = 'minimal-quill',
  error,
  value,
  onChange,
  noFormat = false,
  verySimple = false,
  disabled = false,
  simple = false,
  removeHyperLink = false,
  helperText,
  sx,
  users,
  handleFileInputChange,
  attachFile = false,
  printChat = false,
  emailSwitch = false,
  sendButton = false,
  sendIcon = false,
  aiHelp = false,
  handleSend,
  ticketEmailsOn,
  disableSend = false,
  sendingTicketMessage = false,
  disableSendTooltip = '',
  setTicketEmailsOn = () => null,
  setAddItemsToTicket,
  addItemsToTicket,
  addItemsLabel = 'Add items to ticket',
  currentTicket,
  displayedTicket = currentTicket,
  attachments,
  removeFile,
  shouldFocus,
  chatBoxEditor = false,
  replyMessage,
  onReplyRemove = () => null,
  prependText,
  editorRef,
  ...other
}) {
  const { activeTab } = useTabs();
  const { ticketMessages = [] } = useSelector(
    (state) =>
      state.tickets.data.ticketById[displayedTicket || currentTicket] ||
      state.tickets.data.ticketById[currentTicket] ||
      []
  );

  const baseUrl = window.location.href.includes('&start')
    ? window.location.href.split('&start')[0]
    : window.location.href;
  const sessionStorageKey = `tab-${activeTab.key}-editor-${baseUrl}`;

  const handleChangeMessage = (e) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(e));
    onChange(e);
  };

  const [isDragOver, setIsDragOver] = useState(false);
  const [openCustomLinkSetter, setOpenCustomLinkSetter] = useState(false);

  const internalQuillRef = useRef(null);
  // Use the parent's ref if provided so it can work with the editor directly
  const quillRef = editorRef ?? internalQuillRef;

  const insertImageFiles = useCallback((files) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const range = quill.getSelection(true) ?? { index: quill.getLength() };
        quill.insertEmbed(range.index, 'image', reader.result, 'user');
        quill.setSelection(range.index + 1);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const onDrop = useCallback(
    (acceptedFiles) => {
      setIsDragOver(false);

      const imageFiles = acceptedFiles.filter((file) => file.type?.startsWith('image/'));
      const otherFiles = acceptedFiles.filter((file) => !file.type?.startsWith('image/'));

      if (imageFiles.length) {
        insertImageFiles(imageFiles);
      }

      if (otherFiles.length && typeof handleFileInputChange === 'function') {
        handleFileInputChange({ target: { files: otherFiles } });
      }
    },
    [handleFileInputChange, insertImageFiles]
  );

  const onDragOver = useCallback(() => {
    setIsDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const {
    getRootProps,
    getInputProps,
    open: openDropzone,
  } = useDropzone({
    onDrop,
    onDragOver,
    onDragLeave,
    noClick: true,
    noKeyboard: true,
  });
  const modules = {
    toolbar: {
      container: `#${id}`,
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    syntax: true,
    clipboard: {
      matchVisual: false,
    },
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      isolateCharacter: true,
      mentionDenotationChars: ['@'],
      spaceAfterInsert: true,
      defaultMenuOrientation: 'top',
      positioningStrategy: 'fixed',
      showDenotationChar: false,
      source: useCallback((searchTerm, renderList) => {
        if (searchTerm?.length > 0) {
          const matches = [];
          for (let i = 0; i < users?.length; i++) {
            // Assuming value is in the format "firstName middleName lastName"
            const nameParts = users[i].value.split(' '); // Splits the full name into parts
            const matchFound = nameParts.some((part) => part.toLowerCase().startsWith(searchTerm.toLowerCase()));
            if (matchFound) {
              matches.push(users[i]);
              // Stop searching when 5 results are found
              if (matches.length >= 5) break;
            }
          }
          renderList(matches, searchTerm);
        }
      }, []),
    },
  };

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return undefined;

    const editor = quillRef.current?.editor?.root;
    if (editor) {
      editor.setAttribute('spellcheck', 'true');
    }

    const handleClick = (e) => {
      if (e.target && e.target.tagName === 'A') {
        e.preventDefault();
        const href = e.target.getAttribute('href');
        if (href) {
          window.open(href, '_blank');
          document.querySelectorAll('.ql-tooltip')?.forEach((el) => {
            if (el?.style) el.style = 'none';
          });
        }
      }
    };

    const handlePaste = (e) => {
      const imageFiles = [...(e.clipboardData?.items ?? [])].flatMap((item) => {
        if (item.kind !== 'file' || !item.type?.startsWith('image/')) return [];
        const file = item.getAsFile();
        return file ? [file] : [];
      });

      if (!imageFiles.length) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      insertImageFiles(imageFiles);
    };

    editor.addEventListener('click', handleClick);

    editor.addEventListener('paste', handlePaste, true);

    const editorEl = document.querySelector('.ql-editor');

    const removeHandler = (e) => {
      if (e.target.classList.contains('reply-remove')) {
        const preview = e.target.closest('.reply-preview');
        preview?.remove();
        onReplyRemove();
      }
    };

    editorEl.addEventListener('click', removeHandler);

    let hadReplyBlot = !!quill.root.querySelector('.reply-preview');

    const handleTextChange = () => {
      const hasReplyBlot = !!quill.root.querySelector('.reply-preview');
      if (hadReplyBlot && !hasReplyBlot) {
        onReplyRemove();
      }
      hadReplyBlot = hasReplyBlot;
    };

    quill.on('text-change', handleTextChange);

    // Cleanup
    return () => {
      editor.removeEventListener('click', handleClick);
      editor.removeEventListener('paste', handlePaste, true);
      editorEl.removeEventListener('click', removeHandler);
      quill.off('text-change', handleTextChange);
    };
  }, []);

  useEffect(() => {
    if (shouldFocus) quillRef?.current?.focus();
  }, [shouldFocus]);

  // Insert the reply preview whenever replyMessage changes
  useEffect(() => {
    if (!replyMessage?.text || !replyMessage?.id) return;
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection(true);
    editor.insertEmbed(range.index, 'replyPreview', {
      id: replyMessage.id,
      text: replyMessage.text,
      user: replyMessage.user,
    });
  editor.setSelection(range.index + 1);
  }, [replyMessage]);

  useEffect(() => {
    if (!prependText?.text) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.insertText(0, `${prependText.text}\n`, 'user');
    editor.setSelection(0, 0, 'user');
  }, [prependText]);

  const signature = useSettingsCheck('AutoSignatureOnTickets');
  const defaultMessage = signature?.show ? signature?.value : '';
  const hasMeaningfulContent = (message = '') => {
    const text = message.replace(/<[^>]*>/g, '');
    if (defaultMessage === message || text === '') {
      return false;
    }
    return true;
  };

  return (
    <div
      {...getRootProps()}
      style={{
        width: '100%',
        border: isDragOver && attachFile ? '2px dashed #1976d2' : '2px dashed transparent',
        boxShadow: isDragOver && attachFile ? '0px 0px 10px rgba(25, 118, 210, 0.5)' : 'none',
        transition: 'all 0.2s ease-in-out',
        position: 'relative', // For absolute positioning of the drop message
      }}
    >
      <input {...getInputProps()} />
      {isDragOver && attachFile && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(3px)',
            zIndex: 1, // Make sure it's above the editor content
            pointerEvents: 'none', // Allows interaction with elements below
          }}
        >
          <p style={{ color: '#1976d2', fontSize: '1.2rem', fontWeight: 'bold' }}>Drop files here</p>
        </div>
      )}
      <AIAssistButton value={value} quillRef={quillRef} visible={aiHelp} />
      <Box sx={{ border: '1px solid #c1c9d0', borderRadius: '10px' }}>
        <StyledEditor
          disabled={disabled}
          sx={{
            '& .ql-editor': {
              padding: aiHelp ? '12px 24px' : '12px 18px',
              '&::before': {
                paddingLeft: aiHelp ? '8px' : '0px',
              },
            },
            ...(error && {
              border: (theme) => `solid 1px ${theme.palette.error.main}`,
            }),
            ...sx,
            width: '100%', // Ensure StyledEditor is full width
          }}
        >
          <ReactQuill
            ref={quillRef}
            readOnly={disabled}
            value={value}
            onChange={handleChangeMessage}
            modules={modules}
            formats={formats}
            placeholder="Message..."
            bounds={`#${id}`}
            {...other}
          />
          {attachments?.length > 0 && (
            <Grid container direction="row" alignItems="flex-start">
              {attachments.map((file) => (
                <FileAttachment
                  key={file.name}
                  file={file}
                  fileName={file.name}
                  canDeleteFile
                  handleDeleteFile={removeFile(file)}
                />
              ))}
            </Grid>
          )}
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              // borderRadius: '4px', on the bottom right corner and bottom left corner
              borderRadius: '0 0 10px 10px',
              borderTop: 'solid 1px #c1c9d0',
              backgroundColor: '#fff',
              width: '100%',
              position: 'relative',
              zIndex: 0,
            }}
          >
            <EditorToolbar
              id={id}
              isSimple={simple}
              removeHyperLink={removeHyperLink}
              noFormat={noFormat}
              verySimple={verySimple}
              openDropzone={openDropzone}
              attachFile={attachFile}
              printChat={printChat}
              ticketMessages={ticketMessages}
              chatBoxEditor={chatBoxEditor}
              setOpenCustomLinkSetter={setOpenCustomLinkSetter}
              quillRef={quillRef}
            />
            <PopoutLinkSet
              open={openCustomLinkSetter}
              close={() => setOpenCustomLinkSetter(false)}
              quillRef={quillRef}
            />
            <Grid item>
              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                {emailSwitch && (
                  <FormGroup>
                    <FormControlLabel
                      label={
                        <Tooltip placement="top" arrow title="Email updates to all users for this message">
                          <span>Email updates</span>
                        </Tooltip>
                      }
                      labelPlacement="start"
                      control={
                        <Switch
                          checked={ticketEmailsOn}
                          onChange={() => setTicketEmailsOn((notify) => !notify)}
                          size="small"
                        />
                      }
                      sx={{
                        m: 0,
                        width: 1,
                        justifyContent: 'space-between',
                      }}
                    />
                  </FormGroup>
                )}
                {setAddItemsToTicket && (
                  <Tooltip title={addItemsToTicket ? 'Close selection boxes' : addItemsLabel} placement="top">
                    <IconButton
                      color={addItemsToTicket ? 'warning' : 'secondary'}
                      onClick={() => setAddItemsToTicket(!addItemsToTicket)}
                    >
                      <Iconify icon="fluent:tray-item-add-24-regular" width={18} height={18} />
                    </IconButton>
                  </Tooltip>
                )}
                {sendButton && (
                  <Tooltip title={disableSend ? disableSendTooltip : ''}>
                    <span>
                      <AwaitButton
                        disabled={disableSend || !hasMeaningfulContent(value)}
                        size="small"
                        onClick={handleSend}
                        variant="contained"
                        sx={{
                          minWidth: '100px',
                          mx: 1,
                        }}
                      >
                        Send
                      </AwaitButton>
                    </span>
                  </Tooltip>
                )}
                {sendIcon && (
                  <Tooltip title={disableSend ? disableSendTooltip : ''}>
                    <span>
                      <IconButton
                        disabled={disableSend || sendingTicketMessage || !hasMeaningfulContent(value)}
                        color="primary"
                        onClick={handleSend}
                      >
                        {sendingTicketMessage ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Iconify icon="mdi:send" width={20} height={20} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          </Grid>
        </StyledEditor>
      </Box>
      {helperText && helperText}
    </div>
  );
}
